import { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react'
import { apiClient, setLogoutHandler } from '../services/api'
import { getValidToken, setToken, clearToken } from '../utils/token'
import { resetAnalytics } from '../utils/analytics'

export interface User {
  id: number
  email: string
  full_name: string | null
  is_active: boolean
  is_superuser: boolean
  // Sprint 30 — soft gate: false shows the verify banner; the backend
  // hard-blocks only invite-sending.
  email_verified: boolean
  roles: Array<{ id: number; name: string }>
  organizations?: Array<{ id: number; name: string }> | null
}

/** Two-factor accounts get a challenge instead of a session (2026-09-29:
 *  the web app used to store the empty token and fail silently). */
export type LoginResult = { mfaRequired: false } | { mfaRequired: true; challengeToken: string }

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  /** Finish a two-factor sign-in with an authenticator or backup code. */
  verifyMfa: (challengeToken: string, code: string) => Promise<void>
  /** Start a session from a token issued elsewhere (single sign-on). */
  signInWithToken: (accessToken: string) => Promise<void>
  logout: () => void
  /** Re-fetch /auth/me (e.g. right after verifying the email) so stale
   *  flags like email_verified update without a full reload. */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const setUserRef = useRef(setUser)

  // Keep ref updated with latest setUser
  useEffect(() => {
    setUserRef.current = setUser
  }, [])

  useEffect(() => {
    checkAuth()
    
    // Set up global logout handler for API interceptor
    // Use ref to ensure we always have access to latest setUser
    const logout = () => {
      clearToken()
      setUserRef.current(null)
    }
    setLogoutHandler(logout)
    
    // Cleanup on unmount
    return () => {
      setLogoutHandler(() => {})
    }
  }, [])

  const checkAuth = async () => {
    // getValidToken() returns null (and clears storage) for an expired token,
    // so an expired session never reaches /auth/me and the user is logged out.
    const token = getValidToken()
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const response = await apiClient.get<User>('/api/v1/auth/me')
      setUser(response.data)
    } catch (error) {
      clearToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const signInWithToken = async (accessToken: string) => {
    setToken(accessToken)
    const userResponse = await apiClient.get<User>('/api/v1/auth/me')
    setUser(userResponse.data)
  }

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const response = await apiClient.post<{
      access_token: string; token_type: string; mfa_required?: boolean; challenge_token?: string | null
    }>('/api/v1/auth/login', { email, password })
    if (response.data.mfa_required && response.data.challenge_token) {
      return { mfaRequired: true, challengeToken: response.data.challenge_token }
    }
    await signInWithToken(response.data.access_token)
    return { mfaRequired: false }
  }

  const verifyMfa = async (challengeToken: string, code: string) => {
    const response = await apiClient.post<{ access_token: string }>('/api/v1/auth/mfa/verify', {
      challenge_token: challengeToken, code: code.replace(/\s+/g, ''),
    })
    await signInWithToken(response.data.access_token)
  }

  const logout = () => {
    clearToken()
    setUser(null)
    resetAnalytics()
  }

  const refreshUser = async () => {
    if (!getValidToken()) return
    try {
      const response = await apiClient.get<User>('/api/v1/auth/me')
      setUser(response.data)
    } catch {
      // Keep the current user — a transient failure here must not log out.
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, signInWithToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

/**
 * The signed-in user's organization id (their first/primary membership).
 * Every org-scoped call (labels, custom fields, onboarding) must use this —
 * a hardcoded id only worked in the old single-tenant build and 404s for
 * every SaaS tenant. Falls back to 1 for single-tenant installs whose users
 * carry no organization list; null when nobody is signed in or outside
 * AuthProvider (tests), so callers can skip the request.
 */
export function useCurrentOrgId(): number | null {
  const context = useContext(AuthContext)
  const user = context?.user
  if (!user) return null
  return user.organizations?.[0]?.id ?? 1
}
